import { createSlice } from "@reduxjs/toolkit";
import { StoreDispatch } from "../store";
import {
  getActivity,
  searchAsset,
  searchAssetContent,
  searchAssetContentRequest,
  searchAssetRequest,
  settleTransaction
} from "src/network/graphql/transactionService";
import { InternalFiles } from "../internalFile/internalIFileSlice";
import { initialWalletDetail } from "../walletDetail/walletDetailSlice";
import { getWalletDetail } from "src/network/graphql/walletService";
import { convertToCSV, dateTimeFormat, formatFullName } from "src/utils";
import { immediateToast } from "izitoast-react";
import { EMPTY_STRING } from "src/components/constant/constant";

const flattenObject = (obj: any, prefix = EMPTY_STRING, result: Record<string, any> = {}): Record<string, any> => {
  if (obj === null || obj === undefined) {
    result[prefix] = EMPTY_STRING;
    return result;
  }

  if (typeof obj !== "object" || Array.isArray(obj)) {
    let value = obj;

    if (typeof obj === "string" && obj.length > 6 && /[T\-/:]/.test(obj) && !isNaN(Date.parse(obj))) {
      value = dateTimeFormat(obj)?.datetime || obj;
    }

    result[prefix] = value;
    return result;
  }

  Object.keys(obj).forEach((key) => {
    const newKey = prefix ? `${prefix}.${key}` : key;

    flattenObject(obj[key], newKey, result);
  });

  return result;
};

export type InternalFilesContent = {
  contentId: string;
  assetType: string;
  processStatus: string;
  row: string;
  novattiAmount: string;
  spendableAmount: string;
  ppan: string;
  approvalCode: string;
  settlementStatus: string;
  linkId: string;
  linkType: string;
  prevBalance: string;
  currentBalance: string;
  data: any;
  errMsg: string;
};

const initialFile: InternalFilesContent[] = [];

const initialFileData: InternalFiles[] = [];

const internalfileContentSlice = createSlice({
  name: "files",
  initialState: {
    loading: false,
    walletDetailLoading: false,
    settleTransactionLoading: false,
    reloadFileContent: false,
    downloadFileContentLoading: false,
    error: null,
    internalfilesContent: {
      list: initialFile,
      size: 0
    },
    internalfilesdetail: {
      list: initialFileData,
      size: 0
    },
    walletDetail: initialWalletDetail
  },
  reducers: {
    fetchFilesContentStart: (state) => {
      state.loading = true;
    },
    fetchFilesContentSuccess: (state, action) => {
      state.loading = false;
      state.internalfilesContent = action.payload;
    },
    fetchFilesContentFail: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchFilesDataStart: (state) => {
      state.loading = true;
    },
    fetchFilesDataSuccess: (state, action) => {
      state.loading = false;
      state.internalfilesdetail = action.payload;
    },
    fetchFilesDataFail: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchWalletDetailStart: (state) => {
      state.walletDetailLoading = true;
    },
    fetchWalletDetailSuccess: (state, action) => {
      state.walletDetailLoading = false;
      state.walletDetail.wallet = action.payload.wallet ?? initialWalletDetail.wallet;
    },
    fetchWalletDetailFail: (state, action) => {
      state.walletDetailLoading = false;
      state.walletDetail = initialWalletDetail;
      state.error = action.payload;
    },
    settleTransactionStart: (state) => {
      state.settleTransactionLoading = true;
    },
    settleTransactionSuccess: (state) => {
      state.settleTransactionLoading = false;
    },
    settleTransactionFail: (state, action) => {
      state.settleTransactionLoading = false;
      state.error = action.payload;
    },
    setReloadFileContent: (state, action) => {
      state.reloadFileContent = action.payload;
    },
    downloadFileContentStart: (state) => {
      state.downloadFileContentLoading = true;
    },
    downloadFileContentSuccess: (state) => {
      state.downloadFileContentLoading = false;
    },
    downloadFileContentFail: (state, action) => {
      state.downloadFileContentLoading = false;
      state.error = action.payload;
    }
  }
});

export const {
  fetchFilesContentStart,
  fetchFilesContentSuccess,
  fetchFilesContentFail,
  fetchFilesDataStart,
  fetchFilesDataSuccess,
  fetchFilesDataFail,
  fetchWalletDetailStart,
  fetchWalletDetailSuccess,
  fetchWalletDetailFail,
  settleTransactionStart,
  settleTransactionSuccess,
  settleTransactionFail,
  setReloadFileContent,
  downloadFileContentStart,
  downloadFileContentSuccess,
  downloadFileContentFail
} = internalfileContentSlice.actions;

export const fetchFilesContentAction = (data: searchAssetContentRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchFilesContentStart());
    try {
      const response = await searchAssetContent(data);
      const structuredResponse = response.data.search_file_content.file_content_list?.map((data: any) => ({
        contentId: data.content_id,
        assetType: data.file_type,
        row: data.attributes.row_num,
        processStatus: data.process_status,
        novattiAmount: data.attributes.novatti_amount,
        spendableAmount: data.attributes.spendable_amount,
        ppan: data.attributes.ppan,
        approvalCode: data.attributes.approval_code,
        settlementStatus: data.attributes.settlement_status,
        linkId: data.link_id,
        linkType: data.link_type,
        prevBalance: data.attributes.balance_before_deposit,
        currentBalance: data.attributes.balance_after_deposit,
        data: JSON.parse(data.data),
        errMsg: data.err_message
      }));

      dispatch(
        fetchFilesContentSuccess({
          list: structuredResponse,
          size: response.data.search_file_content.total_size
        })
      );
    } catch (error) {
      dispatch(fetchFilesContentFail(error));
    }
  };
};

export const fetchFilesDataAction = (data: searchAssetRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchFilesDataStart());
    try {
      const response = await searchAsset(data);
      const structuredResponse = response.data.search_file.files?.map((data: any) => ({
        assetId: data.file_id,
        bucketKey: data.attributes.key,
        bucketName: data.attributes.bucket_name,
        date: data.attributes.date,
        processStatus: data.process_status,
        assetType: data?.file_type,
        fileName: data?.attributes?.original_filename,
        totalRow: data.attributes.total_rows
      }));

      dispatch(
        fetchFilesDataSuccess({
          list: structuredResponse,
          size: response.data.search_file.total_size
        })
      );
    } catch (error) {
      dispatch(fetchFilesDataFail(error));
    }
  };
};

export const fetchWalletDetailAction = (id: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchWalletDetailStart());
    try {
      const response = await getActivity({ activity_id: id });
      const data = JSON.parse(response.data.get_activity.data);
      const walletResponse = await getWalletDetail(data?.wallet_id);
      const wallet = walletResponse.data.get_wallet;
      const structuredWalletResponse = {
        name: wallet?.name,
        description: wallet?.description,
        balance: wallet?.balance,
        id: wallet?.wallet_id,
        displayId: wallet?.display_id,
        participant: wallet?.user
          ? {
              id: wallet.user.user_id,
              fullName: formatFullName(wallet.user?.attributes?.first_name, wallet.user?.attributes?.last_name),
              firstName: wallet.user?.attributes?.first_name ?? "-",
              lastname: wallet.user?.attributes?.last_name ?? "-",
              role: wallet.user?.role ?? "PARTICIPANT",
              email: wallet.user?.email,
              phoneNumber: wallet.user?.phone
            }
          : null,
        group: wallet?.group
          ? {
              group_id: wallet.group.group_id,
              name: wallet.group.name,
              group_type: wallet.group.group_type
            }
          : null
      };

      dispatch(
        fetchWalletDetailSuccess({
          wallet: structuredWalletResponse
        })
      );
      if (cb) cb();
    } catch (error) {
      dispatch(fetchWalletDetailFail(error));
    }
  };
};

export const settleTransactionAction = (id: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(settleTransactionStart());
    try {
      await settleTransaction(id);
      dispatch(settleTransactionSuccess());
      dispatch(setReloadFileContent(true));
      if (cb) cb();
    } catch (error) {
      dispatch(settleTransactionFail(error));
    }
  };
};

export const downloadFileContentAction = (
  fileIds: string[],
  settlementStatus?: string | null,
  processStatus?: string | null,
  singleFileName?: string
) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(downloadFileContentStart());
    try {
      const filters: any = {
        file_ids: fileIds
      };

      if (settlementStatus) {
        filters.settlement_status = [settlementStatus];
      }
      if (processStatus) {
        filters.process_status = [processStatus];
      }

      const response = await searchAssetContent({
        size: 1000,
        from: 0,
        filterContentFilter: filters
      });

      const fileContentList = response.data.search_file_content.file_content_list || [];

      if (fileContentList.length === 0) {
        immediateToast("error", {
          message: "No data available to download",
          timeout: 3000,
          position: "topCenter"
        });
        dispatch(downloadFileContentSuccess());
        return;
      }

      const structuredResponse = fileContentList.map((data: any) => ({
        contentId: data.content_id,
        assetType: data.file_type,
        row: data.attributes.row_num,
        processStatus: data.process_status,
        novattiAmount: data.attributes.novatti_amount,
        spendableAmount: data.attributes.spendable_amount,
        ppan: data.attributes.ppan,
        approvalCode: data.attributes.approval_code,
        settlementStatus: data.attributes.settlement_status,
        linkId: data.link_id,
        linkType: data.link_type,
        prevBalance: data.attributes.balance_before_deposit,
        currentBalance: data.attributes.balance_after_deposit,
        data: JSON.parse(data.data),
        errMsg: data.err_message
      }));

      const flattenedData = structuredResponse.map((item: InternalFilesContent) => {
        const flatItem: Record<string, any> = {
          contentId: item.contentId,
          assetType: item.assetType,
          row: item.row,
          processStatus: item.processStatus,
          novattiAmount: item.novattiAmount,
          spendableAmount: item.spendableAmount,
          ppan: item.ppan,
          approvalCode: item.approvalCode,
          settlementStatus: item.settlementStatus,
          linkId: item.linkId,
          linkType: item.linkType,
          prevBalance: item.prevBalance,
          currentBalance: item.currentBalance,
          errMsg: item.errMsg
        };

        if (item.data && typeof item.data === "object") {
          Object.keys(item.data).forEach((key) => {
            if (key !== "wallet_res") {
              const flattened = flattenObject(item.data[key], `data.${key}`);

              Object.assign(flatItem, flattened);
            }
          });
        }

        return flatItem;
      });

      const csv = convertToCSV(flattenedData);
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.setAttribute("hidden", EMPTY_STRING);
      a.setAttribute("href", url);
      let filename = "system_file_content.csv";

      if (fileIds.length === 1) {
        if (singleFileName && singleFileName.trim() !== EMPTY_STRING) {
          filename = `system_${singleFileName.replace(/\\s+/g, "_")}`;
        } else {
          filename = `system_transaction_${fileIds[0]}_${Date.now()}.csv`;
        }
      } else {
        filename = `system_${fileIds.length}_files_${Date.now()}.csv`;
      }

      a.setAttribute("download", filename);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      immediateToast("success", {
        message: "File content downloaded successfully",
        timeout: 3000,
        position: "topCenter"
      });

      dispatch(downloadFileContentSuccess());
    } catch (error) {
      console.error("Error downloading file content:", error);
      immediateToast("error", {
        message: "Failed to download file content",
        timeout: 3000,
        position: "topCenter"
      });
      dispatch(downloadFileContentFail(error));
    }
  };
};

export default internalfileContentSlice.reducer;
